/**
 * Positions-Zusatzfelder einer Bestellung - Unterressource
 * `PATCH /api/orders/:id/items/:jtlPosId` (`backend/src/http/routes/orders.ts:329-365`).
 * Koerperbau wie bei Aufgaben und Raten: `{ rowVersion, felder }`.
 *
 * ============================== WARUM ES DIESES MODUL GIBT ==============================
 *
 * Bis zum 2026-08-13 gab es hier NICHTS, und das war die teuerste Luecke der Naht: die Route stand
 * im Backend samt Fachschicht (`domaene/items.ts`) und sieben Integrationstests, im Frontend rief
 * sie niemand. Jede Eingabe der EK-Tabelle lief stattdessen ueber `EkTab.tsx#positionPatchen` auf
 * `bestellungAendern(id, { items })`, und `"items"` steht in keiner Feldgruppe - RIEGEL 2 warf
 * `FeldOhneGruppeFehler`, bevor ueberhaupt etwas gesendet wurde. Laut, aber wirkungslos: KEINE
 * Positionseingabe war je speicherbar.
 *
 * ============================== DIE ZWEI SCHREIBWEISEN ==============================
 *
 * Dieselbe Asymmetrie wie bei `api/lieferantenExtras.ts`, und aus demselben Grund:
 *
 *   Lesen    `GET /api/orders/:id` liefert `items[]` durch `oeffentlich()`, also in CAMELCASE
 *            (`foc_qty` -> `focQty`, `customs_tariff` -> `customsTariff`).
 *   Schreiben `schreibeItemExtras` prueft JEDEN Feldnamen gegen `ITEM_FELDER`, also gegen die
 *            SNAKE_CASE-Spaltennamen, und wirft sonst `FeldNichtInGruppeError` (400).
 *
 * Ein Formular, das seine gelesenen Schluessel unveraendert zurueckschriebe, traefe bei dreizehn
 * von achtzehn Feldern ins Leere. Die fuenf einsilbigen (`foc`, `manual`, `ean`) faenden ihr Ziel -
 * genau die, an denen der Fehler NICHT auffiele. Deshalb steht die Zuordnung unten AUSDRUECKLICH
 * Feld fuer Feld da und wird nicht mechanisch aus dem Namen gerechnet: eine mechanische Umformung
 * ist genau dann still falsch, wenn die beiden Namen sich nicht nur in der Schreibweise
 * unterscheiden - und sie ist nicht pruefbar gegen die Backend-Liste.
 *
 * ============================== WAS HIER BEWUSST FEHLT ==============================
 *
 * `qty` und `price` stehen NICHT in dieser Tabelle, obwohl `PositionsTabelle.tsx:186` ein `qty`
 * schreibt. Sie sind keine Tool-Felder: Menge und EK-Preis einer Position leben in JTL
 * (`tLieferantenBestellungPos.fMenge`/`fEKNetto`, siehe `backend/src/jtl/bestellungen.ts:114-116`),
 * und `order_item_extras` traegt ausschliesslich die tool-eigenen Zusatzfelder. Ein Schreibversuch
 * auf `qty` scheitert deshalb hier LAUT mit `PositionsfeldNichtSchreibbarFehler` - das ist die
 * richtige Antwort und keine Regression: speicherbar war er noch nie. Fuer die Menge gibt es die
 * tool-eigenen `qtyPl` und `qtyEffOverride`; welche davon die Mengeneingabe der EK-Tabelle treffen
 * soll, ist eine offene Fachfrage und steht in docs/OFFENE-PUNKTE.md.
 *
 * KEIN Ladeweg in diesem Modul: `items[]` kommt seit dem 2026-08-13 mit der Bestellung mit (beide
 * Routen, `domaene/orders.ts#ladeUnterressourcen`). Ein zweiter Abruf ueber
 * `GET /api/orders/:id/items` waere ein zweiter Wahrheitsort fuer dieselben Zeilen.
 */
import { anfrage } from "./basis.ts";

/** Ein Positions-Zusatzfeld in beiden Schreibweisen - siehe "DIE ZWEI SCHREIBWEISEN" oben. */
export interface PositionsFeld {
  /** Spaltenname, wie ihn `PATCH .../items/:jtlPosId` verlangt (snake_case, `ITEM_FELDER`). */
  readonly schreibname: string;
  /** Schluessel, unter dem die Bestellung den Wert in `items[]` liefert (camelCase). */
  readonly lesename: string;
}

/**
 * Die achtzehn Positions-Zusatzfelder, in der Reihenfolge von `ITEM_FELDER`
 * (`backend/src/domaene/schreibbareFelder.ts:95-...`).
 *
 * Die letzten vier sind die Kostenspalten aus Migration 008 (Entscheidung 19 des Auftraggebers vom
 * 2026-08-13). Sie heissen GENAUSO wie ihre Gegenstuecke auf `order_extras` und meinen dieselbe
 * Groesse eine Ebene tiefer - der Bestellbetrag ist die Summe, ein Positionsbetrag die
 * Verfeinerung, der Rest wird verteilt (`lib/landedCost.ts:338-356`).
 *
 * `sort_index` fehlt bewusst und ist KEIN Vergessen: die Reihenfolge wird als GANZES gesetzt
 * (`PUT /item-order`), nie je Position einzeln - sonst entstuenden Luecken und Doppelungen. Die
 * Backend-Liste laesst sie aus demselben Grund aus.
 */
export const POSITIONS_FELDER: readonly PositionsFeld[] = Object.freeze([
  { schreibname: "foc", lesename: "foc" },
  { schreibname: "foc_qty", lesename: "focQty" },
  { schreibname: "manual", lesename: "manual" },
  { schreibname: "qty_pl", lesename: "qtyPl" },
  { schreibname: "qty_eff_override", lesename: "qtyEffOverride" },
  { schreibname: "duty_pct_jtl", lesename: "dutyPctJtl" },
  { schreibname: "customs_tariff", lesename: "customsTariff" },
  { schreibname: "ean", lesename: "ean" },
  { schreibname: "ek_old", lesename: "ekOld" },
  { schreibname: "ek_new_override", lesename: "ekNewOverride" },
  { schreibname: "ek_de_override", lesename: "ekDeOverride" },
  { schreibname: "stock_old", lesename: "stockOld" },
  { schreibname: "source_amount", lesename: "sourceAmount" },
  { schreibname: "customs_group_amount", lesename: "customsGroupAmount" },
  { schreibname: "supplier_invoice", lesename: "supplierInvoice" },
  { schreibname: "customs_invoice", lesename: "customsInvoice" },
  { schreibname: "freight_invoice", lesename: "freightInvoice" },
  { schreibname: "tooling_cost", lesename: "toolingCost" },
] as const);

/** Die achtzehn Schreibnamen als blanke Liste - der Riegel unten und die Tests pruefen dagegen. */
export const POSITIONS_SCHREIBNAMEN: readonly string[] = Object.freeze(
  POSITIONS_FELDER.map((feld) => feld.schreibname),
);

/** camelCase-Lesename -> snake_case-Schreibname. `Map`, kein Objektliteral: der Schluessel stammt
 *  aus einem Aenderungsobjekt der Oberflaeche, also aus Fremddaten - ein Objekt-Lookup griffe bei
 *  `"constructor"` oder `"__proto__"` auf `Object.prototype` durch. Dasselbe Muster wie
 *  `ENTITAET_ZU_FELD` in `state/reduzierer.ts`. */
const LESE_ZU_SCHREIBNAME: ReadonlyMap<string, string> = new Map(
  POSITIONS_FELDER.map((feld) => [feld.lesename, feld.schreibname]),
);

/**
 * Ein Feldname, den `PATCH .../items/:jtlPosId` nicht kennt - dieselbe Absicht wie
 * `FeldOhneGruppeFehler` in `api/bestellungen.ts` und `FeldNichtSchreibbarFehler` in
 * `api/lieferantenExtras.ts`: laut scheitern statt still weglassen, und mit einer Meldung, die den
 * Weg zeigt statt nur "400" zu sagen.
 */
export class PositionsfeldNichtSchreibbarFehler extends Error {
  readonly felder: readonly string[];

  constructor(felder: readonly string[]) {
    const erlaubte = POSITIONS_FELDER.map((f) => f.lesename).join(", ");
    super(
      (felder.length === 0
        ? "positionAendern: leeres Aenderungsobjekt - der Server wiese es mit 400 ab. "
        : `positionAendern: ${felder.map((f) => `"${f}"`).join(", ")} ` +
          `${felder.length === 1 ? "ist kein" : "sind keine"} schreibbares Positions-Zusatzfeld. `) +
        `Schreibbar sind: ${erlaubte}. ` +
        "Menge und EK-Preis liegen in JTL und sind hier grundsaetzlich nicht schreibbar - " +
        "siehe Kopfkommentar, \"WAS HIER BEWUSST FEHLT\".",
    );
    this.name = "PositionsfeldNichtSchreibbarFehler";
    this.felder = Object.freeze([...felder]);
  }
}

/** Eine anzulegende Positionszeile - die Form, die `POST /api/orders/:id/items` verlangt. */
export interface NeuePositionszeile {
  readonly jtlPosId: number;
  readonly sortIndex: number;
  /** SNAKE_CASE wie bei `positionAendern` - dieselbe Pruefung gegen `ITEM_FELDER` im Backend. */
  readonly felder: Record<string, unknown>;
}

/**
 * Ordnet die Zeilen der Anlagemaske den frisch entstandenen JTL-Positionen zu.
 *
 * ================= WARUM UEBER DEN INDEX UND NICHT UEBER DIE SKU =================
 *
 * Beides waere denkbar, und beides kann schiefgehen - aber verschieden schlimm. Die Maske erlaubt
 * dieselbe Artikelnummer zweimal (zwei Zeilen, zwei Liefertermine); eine Zuordnung ueber die SKU
 * traefe dann willkuerlich eine der beiden. Der Index ist dagegen exakt, solange JTL die Positionen
 * in der gesendeten Reihenfolge anlegt - `positionenAlsXml` schickt sie als geordnete Liste, und
 * `GET /api/jtl/orders/:k` liest sie nach `kLieferantenBestellungPos` sortiert zurueck.
 *
 * "Solange" ist eine Annahme, und ungeprueft waere sie eine schlechte. Deshalb die GEGENPROBE:
 * stimmt die Anzahl nicht oder weicht auch nur EINE Artikelnummer an ihrer Stelle ab, werden die
 * Zeilen OHNE Kennzeichen angelegt. Die Zeile selbst ist der eigentliche Zweck - ohne sie ist
 * spaeter gar nichts schreibbar. Ein M an der FALSCHEN Position waere dagegen ein stiller Fehler
 * mitten in der EK-Rechnung (`lib/landedCost.ts` ueberspringt `manual`-Zeilen), und still falsch
 * ist in diesem Projekt schlimmer als sichtbar unvollstaendig.
 */
export function positionszeilenAus(
  jtlPositionen: readonly { kLieferantenBestellungPos: number; artikelNummer?: unknown }[],
  maskenzeilen: readonly { sku?: unknown; manual?: unknown; foc?: unknown }[],
): { zeilen: NeuePositionszeile[]; kennzeichenUebernommen: boolean } {
  const nummer = (wert: unknown): string => String(wert ?? "").trim().toUpperCase();
  const passt =
    jtlPositionen.length === maskenzeilen.length &&
    jtlPositionen.every((p, i) => nummer(p.artikelNummer) === nummer(maskenzeilen[i]?.sku));

  return {
    kennzeichenUebernommen: passt,
    zeilen: jtlPositionen.map((position, i) => ({
      jtlPosId: position.kLieferantenBestellungPos,
      sortIndex: i,
      felder: passt
        ? { foc: Boolean(maskenzeilen[i]?.foc), manual: Boolean(maskenzeilen[i]?.manual) }
        : {},
    })),
  };
}

/**
 * `POST /api/orders/:id/items` - legt die Positionszeilen an (`order_item_extras`).
 *
 * Der Weg, den es bis zum 2026-08-15 nur in der Migration gab: `schreibeItemExtras` ist ein reines
 * `UPDATE`, eine Bestellung aus der Anlagemaske hatte also gar keine Zeile, die `positionAendern`
 * haette treffen koennen. Idempotent - ein zweiter Aufruf mit derselben Liste laesst bestehende
 * Zeilen unangetastet (siehe `domaene/items.ts#legePositionszeilenAn`).
 *
 * Liefert ALLE Positionszeilen der Bestellung in oeffentlicher Form, jede mit ihrer `rowVersion` -
 * genau das, was `positionAendern` danach braucht.
 */
export function positionszeilenAnlegen(
  bestellungId: string,
  positionen: readonly NeuePositionszeile[],
): Promise<Record<string, unknown>[]> {
  return anfrage<Record<string, unknown>[]>(
    `/api/orders/${encodeURIComponent(bestellungId)}/items`,
    { methode: "POST", koerper: { positionen } },
  );
}

/**
 * `PATCH /api/orders/:id/items/:jtlPosId`. `aenderung` traegt camelCase-Feldnamen, wie die
 * Oberflaeche sie aus `items[]` gelesen hat; sie werden hier auf die snake_case-Spalten umgesetzt.
 *
 * Liefert die oeffentliche Positionszeile nach dem Schreiben (camelCase, samt neuer `rowVersion`) -
 * der Aufrufer haelt damit seine `rowVersion` nach, sonst laeuft der zweite Schreibvorgang auf
 * dieselbe Position deterministisch in einen 409.
 *
 * `sortIndex` (Task 11, optional, seit Task 12 auch von `EkTab.tsx` mitgefuehrt): reicht
 * `sortIndexFallsNeu` durch (`backend/src/domaene/items.ts`, Route
 * `backend/src/http/routes/orders.ts:452-458`). Er wirkt AUSSCHLIESSLICH, wenn die Zeile beim
 * Schreiben neu angelegt wird (`legeItemExtraAn`) - bei einer bereits bestehenden Zeile ignoriert ihn
 * der Server stillschweigend (nur `updateVersioned` laeuft, das keine `sort_index`-Spalte kennt). Er
 * kann deshalb GEFAHRLOS bei jedem Aufruf mitgeschickt werden, nicht nur beim ersten: sowohl
 * `Positionen.tsx` als auch `EkTab.tsx` kennen den Index ohnehin (denselbe Quelle wie
 * `positionszeilenAus` oben, der Laufindex ueber `o.items`) und muessen nicht selbst unterscheiden, ob
 * die Hub-Zeile schon existiert. Fehlt er (ein Aufrufer ohne den Index), bleibt es bei der bisherigen
 * Vorgabe: die neue Zeile wird hinten angestellt (`COALESCE(MAX(sort_index) + 1, 0)`).
 */
export function positionAendern(
  bestellungId: string,
  jtlPosId: number,
  aenderung: Record<string, unknown>,
  rowVersion: number,
  sortIndex?: number,
): Promise<Record<string, unknown>> {
  const unbekannt = Object.keys(aenderung).filter((name) => !LESE_ZU_SCHREIBNAME.has(name));
  if (unbekannt.length > 0 || Object.keys(aenderung).length === 0) {
    throw new PositionsfeldNichtSchreibbarFehler(unbekannt);
  }

  const felder: Record<string, unknown> = {};
  for (const [lesename, wert] of Object.entries(aenderung)) {
    felder[LESE_ZU_SCHREIBNAME.get(lesename) as string] = wert;
  }

  return anfrage<Record<string, unknown>>(
    `/api/orders/${encodeURIComponent(bestellungId)}/items/${encodeURIComponent(String(jtlPosId))}`,
    {
      methode: "PATCH",
      koerper: sortIndex === undefined ? { rowVersion, felder } : { rowVersion, felder, sortIndex },
    },
  );
}
